import React, { useMemo, useState } from 'react'
import { CircularProgress, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { touServices } from 'services/touServices'
import {
  AcceptButton,
  ActionsContainer,
  ContentPaper,
  PageContainer,
  TOUWrapper,
} from './Legal.styled'
import { onLogout } from 'services'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { resetProfileOnSignOut } from 'store/slices/userSlice'
import { successToast } from 'utils/toastService'
import { Button } from '../../components'
import { isBakerModule } from '../../utils/helpers/isBakerModule'
import { getDefaultRoute } from '../../utils'

const TOUPage: React.FC = () => {
  const { getCurrentTOU, getTOUContent, acceptTOU } = touServices()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [versionId, setVersionId] = useState<number | null>(null)

  const { data: currentTOU, isLoading: loadingTOU } = useQuery(
    'currentTOU',
    getCurrentTOU,
    {
      onSuccess: (data) => setVersionId(data.id),
      retry: false,
    }
  )

  const { data: htmlContent, isLoading: loadingContent } = useQuery(
    ['touContent', versionId],
    () => getTOUContent(versionId!),
    { enabled: !!versionId, retry: false }
  )

  const { mutate: acceptMutation, isLoading: accepting } = useMutation(
    () => acceptTOU({ tou_version_id: versionId! }),
    {
      onSuccess: () => {
        void queryClient.invalidateQueries('currentTOU')
        window.location.href = getDefaultRoute()
        successToast('You have accepted the Terms of Use.')
      },
    }
  )

  const { mutate: logoutMutation, isLoading: loggingOut } = useMutation(
    onLogout,
    {
      onSuccess: () => {
        localStorage.removeItem('token')
        dispatch(resetProfileOnSignOut())
        navigate('/auth/login')
        successToast('Logged out successfully.')
      },
      onError: () => {
        localStorage.removeItem('token')
        navigate('/auth/login')
      },
    }
  )

  const handleAccept = () => {
    if (!versionId) return
    acceptMutation()
  }

  const handleDecline = () => {
    logoutMutation()
  }

  const updatedHtmlContent = useMemo(
    () =>
      isBakerModule
        ? (htmlContent?.replace('Wheat', 'Bakery') ?? '')
        : htmlContent,
    [htmlContent]
  )

  if (loadingTOU || loadingContent) {
    return (
      <PageContainer>
        <CircularProgress />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <TOUWrapper>
        <Typography align="center" variant="h4" gutterBottom>
          Manna™ AI for {isBakerModule ? 'Bakery' : 'Wheat'}
        </Typography>
        <Typography align="center" variant="h5" gutterBottom>
          {currentTOU?.title ?? 'Terms of Use'}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Version: {currentTOU?.version_string}
        </Typography>

        <ContentPaper>
          {updatedHtmlContent ? (
            <div
              style={{
                maxHeight: '70vh',
                overflowY: 'auto',
                padding: '1rem',
                lineHeight: 1.6,
              }}
              dangerouslySetInnerHTML={{ __html: updatedHtmlContent }}
            />
          ) : (
            <Typography p={2} color="error">
              Could not load Terms of Use
            </Typography>
          )}
        </ContentPaper>

        <ActionsContainer>
          <AcceptButton
            variant="contained"
            color="primary"
            onClick={handleAccept}
            disabled={accepting}
          >
            {accepting ? <CircularProgress size={20} /> : 'I Accept'}
          </AcceptButton>
          <Button
            variantType="secondary"
            onClick={handleDecline}
            disabled={loggingOut}
          >
            {loggingOut ? <CircularProgress size={20} /> : 'Decline'}
          </Button>
        </ActionsContainer>
      </TOUWrapper>
    </PageContainer>
  )
}

export default TOUPage
